import { useEffect, useRef } from "react";
import {
  ARKIT52_MORPHS,
  type ARKit52Morph,
  type ElevenLabsAlignment,
  getActiveVisemeAt,
  NATIVE_VISEME_MORPHS,
  type NativeVisemeMorph,
  getActiveNativeVisemeAt,
} from "@/lib/visemeMapper";
import {
  smoothVisemeAsymmetric,
  ATTACK_TAU_MS,
  RELEASE_TAU_MS,
  DT_CLAMP_MAX_MS,
  SILENCE_THRESHOLD,
} from "@/lib/audioLipSync";
import type { SpeechPerformanceFrame } from "@/lib/language/avatarPerformance";
import { getPreparedSpeech, readSpeechEnvelope, SPEECH_ENVELOPE_HOP_MS as HOP_MS } from "@/lib/speechAudio";
import { mouthShapeAt, nativeMouthShape, arkitMouthShape, type MouthCueTrack } from "@/lib/timedMouthCues";

// Priority: audio-derived timed mouth cues, legacy character-timed heuristic,
// then RMS rhythm fallback. Neither grapheme mapping nor loudness is phoneme
// alignment. Native HTMLAudioElement.currentTime is the single playback clock.
// Offline decode never routes native media through Web Audio.
const ARKIT_MOUTH_MORPHS = ARKIT52_MORPHS.filter(name => /^(jaw|mouth)|^tongueOut$/.test(name));

// Mesh-shaped subset of THREE.Mesh — kept minimal so this hook doesn't
// pull a Three.js dependency. Caller (AvatarCore) passes the morph mesh
// directly.
export interface LipSyncMorphTarget {
  morphTargetDictionary: Record<string, number>;
  morphTargetInfluences: number[];
  /** Optional mesh name for diagnostic logs. */
  name?: string;
}

export interface UseLipSyncOptions {
  audioElement: HTMLAudioElement | null;
  // Legacy character timestamps; a grapheme heuristic, not phoneme alignment.
  alignment?: ElevenLabsAlignment | null;
  morphTarget: LipSyncMorphTarget | null;
  /** Idiomas opt-in: synchronize matching head/teeth meshes in this same driver. */
  additionalMorphTargets?: readonly LipSyncMorphTarget[];
  onPerformanceFrame?: (frame: SpeechPerformanceFrame) => void;
  smoothingAlpha?: number;
  enabled?: boolean;
}

// ── Tuning constants (named, safe to adjust by eye) ───────────────────────────

// Timed mouth-shape intensity.
const VISEME_INTENSITY = 0.8; // global softener — VISEME_MAP weights run hot on a photoreal face
const MAX_JAW_OPEN = 0.4; // hard cap on jawOpen; 0.55–0.6 reads as shouting

// The decoding AudioContext does not own native HTML audio output. Its latency
// is not a valid offset for this media element. Default to its real clock;
// retain explicit dev calibration for measured device-specific adjustments.
const LIPSYNC_PHASE_OFFSET_MS = 0;

// Amplitude fallback path (only when alignment is missing/empty).
const FALLBACK_ENERGY_SCALE = 5; // RMS→energy gain (was 6)
const FALLBACK_JAW_GAIN = 1.1; // softer than the old 1.5
const FALLBACK_JAW_CURVE = 0.7; // <1 → opens fast at low energy, eases near the top
const FALLBACK_MAX_JAW = 0.38; // soft cap (was 0.6 — the flap)

// ── Dev calibration bridge (window.__VYV_LIPSYNC) ─────────────────────────────
// A single global the dev-only LipSyncCalibrationHUD reads/writes so phase, jaw
// cap, intensity and the smoothing τ can be dialed LIVE on device without a
// rebuild. In production the HUD never mounts: the object stays empty, every
// lookup falls back to the constants above, and this costs a few property reads
// per frame. SSR/test-safe (guards `window`).
export interface VyvLipSyncDebug {
  /** Phase offset override (ms). When a finite number, wins over the resolved latency. */
  phaseMs?: number;
  /** Release smoothing τ override (ms) — the lag knob. */
  releaseTauMs?: number;
  /** Attack smoothing τ override (ms). */
  attackTauMs?: number;
  /** jawOpen hard-cap override. */
  maxJaw?: number;
  /** Global viseme weight softener override. */
  intensity?: number;
  /** Telemetry written by the hook: device-reported output latency (ms) or null. */
  measuredOutputLatencyMs?: number | null;
  /** Telemetry written by the hook: the phase offset actually applied (ms). */
  effectivePhaseMs?: number;
}

declare global {
  interface Window {
    __VYV_LIPSYNC?: VyvLipSyncDebug;
  }
}

// Lazily-initialized accessor for the shared debug object. Returns a throwaway
// object off-window (SSR/tests) so callers never branch on null.
function getLipSyncDebug(): VyvLipSyncDebug {
  if (typeof window === "undefined") return {};
  return (window.__VYV_LIPSYNC ??= {});
}

// Returns `v` when it is a finite number, else `fallback`. Lets HUD overrides
// (unknown at runtime) coexist with typed defaults.
function numOr(v: unknown, fallback: number): number {
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

function validCharacterAlignment(value: ElevenLabsAlignment | null | undefined): boolean {
  if (!value || !Array.isArray(value.characters) || !value.characters.length
    || !Array.isArray(value.character_start_times_seconds) || !Array.isArray(value.character_end_times_seconds)
    || value.characters.length !== value.character_start_times_seconds.length
    || value.characters.length !== value.character_end_times_seconds.length) return false;
  let end = 0;
  return value.characters.every((char, i) => {
    const start = value.character_start_times_seconds[i], next = value.character_end_times_seconds[i];
    if (typeof char !== "string" || !Number.isFinite(start) || !Number.isFinite(next)
      || start < end || next < start) return false;
    end = next; return true;
  });
}

// Soften + cap the mapped mouth weights for a photoreal face.
// `intensity` and `maxJaw` are passed in (not closed over the module constants)
// so the dev HUD can adjust them live; production passes the constants.
function scaleVisemeTarget(
  raw: Partial<Record<ARKit52Morph, number>>,
  intensity: number,
  maxJaw: number,
): Partial<Record<ARKit52Morph, number>> {
  const out: Partial<Record<ARKit52Morph, number>> = {};
  for (const key in raw) {
    const morph = key as ARKit52Morph;
    let v = (raw[morph] ?? 0) * intensity;
    if (morph === "jawOpen") v = Math.min(v, maxJaw);
    out[morph] = v;
  }
  return out;
}

// Amplitude-only mouth weights (fallback): jaw opens with loudness, soft-capped.
function amplitudeFallbackTarget(energy: number): Partial<Record<ARKit52Morph, number>> {
  if (energy < SILENCE_THRESHOLD) return {};
  const jaw = Math.min(
    FALLBACK_MAX_JAW,
    Math.pow(energy, FALLBACK_JAW_CURVE) * FALLBACK_JAW_GAIN,
  );
  return { jawOpen: jaw };
}

// Native amplitude fallback is a softened open-vowel rhythm, not phonetic
// articulation. Used only when the clip has neither audio-derived cues nor
// legacy character timestamps. Native rigs have no ARKit-only mouth poses.
const NATIVE_FALLBACK_AA_MAX = 0.7; // portador; alignment path chega a 0.85
const NATIVE_FALLBACK_TOP_KNEE = 0.6; // acima desta energia, E/oh entram
const NATIVE_FALLBACK_E_MAX = 0.18;
const NATIVE_FALLBACK_OH_MAX = 0.14;

// Exportada para o teste (src/test/lipSyncNativeFallback.test.ts) exercitar a
// curva sem montar o hook. NUNCA emite jawOpen — pinado lá.
export function amplitudeNativeFallbackTarget(
  energy: number,
): Partial<Record<NativeVisemeMorph, number>> {
  if (energy < SILENCE_THRESHOLD) return {};
  const drive = Math.min(
    1,
    Math.pow(energy, FALLBACK_JAW_CURVE) * FALLBACK_JAW_GAIN,
  );
  const target: Partial<Record<NativeVisemeMorph, number>> = {
    aa: drive * NATIVE_FALLBACK_AA_MAX,
  };
  const top = (energy - NATIVE_FALLBACK_TOP_KNEE) / (1 - NATIVE_FALLBACK_TOP_KNEE);
  if (top > 0) {
    target.E = top * NATIVE_FALLBACK_E_MAX;
    target.oh = top * NATIVE_FALLBACK_OH_MAX;
  }
  return target;
}

// ── Viseme channel feature-detection ──────────────────────────────────────────
// Export configurations differ. The 11 current MetaPerson heads expose native
// visemes and ARKit facial shapes; their lower teeth expose the native set.
// Prefer the native speech channel when present, otherwise use ARKit. Inspect
// the actual loaded dictionary instead of assuming a provider-wide morph set.
// Never layer both jaw/speech channels on the same mesh.
//
// Discriminator: presence of PP + FF + an O/U rounder (oh|ou). These are native-
// only names an ARKit rig never carries, so a false positive is not possible;
// anything else falls through to the ARKit path (unchanged behaviour).
type LipChannel = "native" | "arkit";

function detectChannel(dict: Record<string, number>): LipChannel {
  const has = (n: string) => dict[n] !== undefined;
  return has("PP") && has("FF") && (has("oh") || has("ou")) ? "native" : "arkit";
}

// Soften the raw native-viseme weights by `intensity` (same knob the ARKit path
// uses). No jawOpen cap here: the native visemes are self-contained poses that
// already encode their own jaw drop, and jawOpen is NOT in NATIVE_VISEME_MORPHS.
function scaleNativeTarget(
  raw: Partial<Record<NativeVisemeMorph, number>>,
  intensity: number,
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const key in raw) {
    out[key] = (raw[key as NativeVisemeMorph] ?? 0) * intensity;
  }
  return out;
}

// Native twin of smoothVisemeAsymmetric (audioLipSync.ts): same frame-rate-
// independent asymmetric attack/release, but iterating NATIVE_VISEME_MORPHS.
// A local copy is required because the shared smoother loops the fixed
// ARKIT52_MORPHS and audioLipSync.ts is out of this change's scope.
function smoothNativeAsymmetric(
  current: Record<string, number>,
  target: Record<string, number>,
  dtMs: number,
  attackTauMs: number,
  releaseTauMs: number,
): Record<string, number> {
  const attackAlpha = 1 - Math.exp(-dtMs / attackTauMs);
  const releaseAlpha = 1 - Math.exp(-dtMs / releaseTauMs);
  const result: Record<string, number> = {};
  for (const morph of NATIVE_VISEME_MORPHS) {
    const cur = current[morph] ?? 0;
    const tgt = target[morph] ?? 0;
    const alpha = tgt > cur ? attackAlpha : releaseAlpha;
    result[morph] = cur + (tgt - cur) * alpha;
  }
  return result;
}

let nextSpeechGeneration = 0;

export function matchingLipSyncTargets(primary: LipSyncMorphTarget, extras: readonly LipSyncMorphTarget[] = []) {
  const channel = detectChannel(primary.morphTargetDictionary);
  const seen = new Set<number[]>();
  return [primary, ...extras].filter(target => {
    if (seen.has(target.morphTargetInfluences) || detectChannel(target.morphTargetDictionary) !== channel) return false;
    seen.add(target.morphTargetInfluences); return true;
  });
}

// Drives the GLB's mouth morphs each animation frame — viseme-first, amplitude
// fallback. Self-contained: pause/end reset to neutral; missing alignment AND
// missing envelope both yield a closed mouth and never throw.
export function useLipSync({
  audioElement,
  alignment,
  morphTarget,
  additionalMorphTargets,
  onPerformanceFrame,
  // `smoothingAlpha` stays in UseLipSyncOptions for call-site compat but is
  // unused — per-frame smoothing uses ATTACK_TAU_MS / RELEASE_TAU_MS.
  enabled = true,
}: UseLipSyncOptions): void {
  const performanceCallback = useRef(onPerformanceFrame);
  performanceCallback.current = onPerformanceFrame;
  const rafRef = useRef<number | null>(null);
  // Holds the last smoothed weights. Widened to Record<string, number> so it can
  // carry EITHER ARKit names or native viseme names, depending on the detected
  // channel; only one channel's keys are ever present at a time.
  const currentWeightsRef = useRef<Record<string, number>>({});
  const lastFrameTimeRef = useRef<number | null>(null);
  const envelopeRef = useRef<Float32Array | null>(null);
  const decodedSrcRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled || !audioElement || !morphTarget) {
      currentWeightsRef.current = {};
      return;
    }
    let disposed = false;
    const targets = matchingLipSyncTargets(morphTarget, additionalMorphTargets);
    let generation = ++nextSpeechGeneration;
    let observedSource = audioElement.currentSrc || audioElement.src || "";
    const report = (state: SpeechPerformanceFrame['state']) => {
      if (!performanceCallback.current) return;
      const env = envelopeRef.current;
      const index = Math.floor(audioElement.currentTime * 1000 / HOP_MS);
      const rms = env && index >= 0 && index < env.length ? env[index] : null;
      const shape = mouthCues ? mouthShapeAt(mouthCues, audioElement.currentTime) : null;
      performanceCallback.current({
        utteranceId: generation, seconds: Number.isFinite(audioElement.currentTime) ? audioElement.currentTime : 0,
        state, source: mouthCues ? 'authored-shapes' : hasAlignment ? 'character-timing' : env ? 'amplitude' : 'none',
        activity: state !== 'playing' ? 0 : rms !== null ? Math.max(0, Math.min(1, rms * FALLBACK_ENERGY_SCALE))
          : shape && shape !== 'X' ? .22 : 0,
      });
    };

    const hasAlignment = validCharacterAlignment(alignment);
    let mouthCues: MouthCueTrack | null = null;
    const resolvedPhaseMs = LIPSYNC_PHASE_OFFSET_MS;
    getLipSyncDebug().measuredOutputLatencyMs = null;

    // Decide the mouth channel ONCE per mounted mesh (the effect re-runs when
    // `morphTarget` changes, so this caches per avatar). Everything below drives
    // exactly this channel's names — never both.
    const dict = morphTarget.morphTargetDictionary;
    const channel = detectChannel(dict);
    const channelNames = channel === "native" ? NATIVE_VISEME_MORPHS : ARKIT_MOUTH_MORPHS;

    // Self-diagnostic: count how many of the chosen channel's MOUTH-SHAPE names
    // actually exist in this mesh's dictionary (jawOpen excluded — it's the one
    // name shared by both rigs and alone it can only flap, not shape). When the
    // coverage is under the threshold, the per-frame writes would be silent
    // no-ops, so we surface the exact miss on the first load instead of shipping
    // a mute mouth. Reuses the same dict AvatarCore enumerates in its
    // [lipsync-debug] mesh log.
    const MOUTH_SHAPE_MIN = 3;
    let mouthShapeCoverage = 0;
    for (const morph of channelNames) {
      if (morph === "jawOpen") continue;
      if (dict[morph] !== undefined) mouthShapeCoverage++;
    }
    if (mouthShapeCoverage < MOUTH_SHAPE_MIN) {
      console.warn(
        "[lipsync-diag] low mouth-shape coverage — lip-sync will barely move." +
          " mesh=" + (morphTarget.name ?? "(unnamed)") +
          " channel=" + channel +
          " matched=" + mouthShapeCoverage + "/" + (channelNames.length - 1) +
          " (need >=" + MOUTH_SHAPE_MIN + "). The rig likely exports a different" +
          " blendshape set than the driven channel.",
        { channel, mouthShapeCoverage, dictKeys: Object.keys(dict) },
      );
    }

    const resetToNeutral = () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      for (const target of targets) for (const morph of channelNames) {
        const idx = target.morphTargetDictionary[morph];
        if (idx !== undefined) target.morphTargetInfluences[idx] = 0;
      }
      report(audioElement.ended ? 'ended' : audioElement.paused ? 'paused' : 'waiting');
      currentWeightsRef.current = {};
      lastFrameTimeRef.current = null;
    };

    // Prepared audio carries cues and an optional envelope from the same bytes.
    // Legacy alignment needs no fallback decode; other URLs decode RMS once.
    const syncEnvelope = () => {
      const source = audioElement.currentSrc || audioElement.src || "";
      if (source !== observedSource) {
        observedSource = source; generation = ++nextSpeechGeneration;
        if (additionalMorphTargets || performanceCallback.current) {
          for (const target of targets) for (const name of channelNames) {
            const index = target.morphTargetDictionary[name];
            if (index !== undefined) target.morphTargetInfluences[index] = 0;
          }
          currentWeightsRef.current = {};
        }
      }
      const bound = getPreparedSpeech(audioElement);
      if (bound) {
        mouthCues = bound.mouthCues;
        envelopeRef.current = bound.envelope;
        decodedSrcRef.current = bound.url;
        return;
      }
      mouthCues = null;
      if (hasAlignment) return;
      const src = audioElement.currentSrc || audioElement.src || "";
      if (!src || src === decodedSrcRef.current) return;
      decodedSrcRef.current = src;
      envelopeRef.current = null;
      void readSpeechEnvelope(src).then((env) => {
        if (!disposed && decodedSrcRef.current === src && (audioElement.currentSrc || audioElement.src) === src) {
          envelopeRef.current = env;
          // A transient decode failure may be retried on the next play.
          if (!env) decodedSrcRef.current = null;
        }
      });
    };

    const tick = (frameTime: number) => {
      const last = lastFrameTimeRef.current;
      const dtMs = last === null ? 16.7 : Math.max(0, Math.min(DT_CLAMP_MAX_MS, frameTime - last));
      lastFrameTimeRef.current = frameTime;

      // Read live overrides once per frame (no-op object in production).
      const dbg = getLipSyncDebug();

      report(!audioElement.paused && !audioElement.ended ? 'playing' : audioElement.ended ? 'ended' : 'paused');
      // Native and ARKit channels share the same cue/media clock and smoothing.
      if (channel === "native") {
        let nativeTarget: Record<string, number> = {};
        if (!audioElement.paused && !audioElement.ended) {
          if (mouthCues) {
            const phaseMs = numOr(dbg.phaseMs, resolvedPhaseMs);
            dbg.effectivePhaseMs = phaseMs;
            const shape = mouthShapeAt(mouthCues, audioElement.currentTime - phaseMs / 1000);
            nativeTarget = scaleNativeTarget(nativeMouthShape(shape), numOr(dbg.intensity, VISEME_INTENSITY));
          } else if (hasAlignment) {
            const phaseMs = numOr(dbg.phaseMs, resolvedPhaseMs);
            dbg.effectivePhaseMs = phaseMs;
            const t = Math.max(0, audioElement.currentTime - phaseMs / 1000);
            nativeTarget = scaleNativeTarget(
              getActiveNativeVisemeAt(t, alignment!),
              numOr(dbg.intensity, VISEME_INTENSITY),
            );
          } else {
            // FALLBACK por amplitude (sem phase offset, como no ARKit): o
            // envelope já foi decodado pelo syncEnvelope — ele é agnóstico de
            // canal e só era ignorado aqui.
            const env = envelopeRef.current;
            if (env) {
              const idx = Math.floor((audioElement.currentTime * 1000) / HOP_MS);
              const rms = idx >= 0 && idx < env.length ? env[idx] : 0;
              const energy = Math.min(1, rms * FALLBACK_ENERGY_SCALE);
              nativeTarget = scaleNativeTarget(
                amplitudeNativeFallbackTarget(energy),
                numOr(dbg.intensity, VISEME_INTENSITY),
              );
            }
          }
        }
        const smoothedNative = smoothNativeAsymmetric(
          currentWeightsRef.current,
          nativeTarget,
          dtMs,
          numOr(dbg.attackTauMs, ATTACK_TAU_MS),
          numOr(dbg.releaseTauMs, RELEASE_TAU_MS),
        );
        currentWeightsRef.current = smoothedNative;
        for (const target of targets) for (const morph of NATIVE_VISEME_MORPHS) {
          const idx = target.morphTargetDictionary[morph];
          if (idx !== undefined) target.morphTargetInfluences[idx] = Math.min(1, Math.max(0, smoothedNative[morph] ?? 0));
        }
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      let target: Partial<Record<ARKit52Morph, number>> = {};
      if (!audioElement.paused && !audioElement.ended) {
        if (mouthCues) {
          const phaseMs = numOr(dbg.phaseMs, resolvedPhaseMs);
          dbg.effectivePhaseMs = phaseMs;
          const shape = mouthShapeAt(mouthCues, audioElement.currentTime - phaseMs / 1000);
          target = scaleVisemeTarget(arkitMouthShape(shape), numOr(dbg.intensity, VISEME_INTENSITY), numOr(dbg.maxJaw, MAX_JAW_OPEN));
        } else if (hasAlignment) {
          // Legacy character-timed heuristic; not a phoneme alignment.
          const phaseMs = numOr(dbg.phaseMs, resolvedPhaseMs);
          dbg.effectivePhaseMs = phaseMs;
          const t = Math.max(0, audioElement.currentTime - phaseMs / 1000);
          target = scaleVisemeTarget(
            getActiveVisemeAt(t, alignment!),
            numOr(dbg.intensity, VISEME_INTENSITY),
            numOr(dbg.maxJaw, MAX_JAW_OPEN),
          );
        } else {
          // FALLBACK: recalibrated amplitude → jawOpen only. (No phase offset.)
          const env = envelopeRef.current;
          if (env) {
            const idx = Math.floor((audioElement.currentTime * 1000) / HOP_MS);
            const rms = idx >= 0 && idx < env.length ? env[idx] : 0;
            const energy = Math.min(1, rms * FALLBACK_ENERGY_SCALE);
            target = amplitudeFallbackTarget(energy);
          }
        }
      }

      const smoothed = smoothVisemeAsymmetric(
        currentWeightsRef.current,
        target,
        dtMs,
        numOr(dbg.attackTauMs, ATTACK_TAU_MS),
        numOr(dbg.releaseTauMs, RELEASE_TAU_MS),
      );
      currentWeightsRef.current = smoothed;

      for (const target of targets) for (const morph of ARKIT_MOUTH_MORPHS) {
        const idx = target.morphTargetDictionary[morph];
        if (idx !== undefined) target.morphTargetInfluences[idx] = Math.min(1, Math.max(0, smoothed[morph]));
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    const startPlayback = () => {
      syncEnvelope();
      // pause/ended cancel the RAF. Restart it for replay and sentence chains
      // even when React reuses the same audio element and null alignment.
      if (rafRef.current === null && !audioElement.paused && !audioElement.ended) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    startPlayback();

    // A queued event from the previous src may reach a reused native element.
    // Current media state, not just the event name, decides whether to stop.
    const onPause = () => { if (audioElement.paused) resetToNeutral(); };
    const onEnded = () => { if (audioElement.ended) resetToNeutral(); };
    const onError = () => { if (audioElement.error) resetToNeutral(); };
    const onWaiting = () => { if (audioElement.readyState < 3) resetToNeutral(); };
    audioElement.addEventListener("play", startPlayback);
    audioElement.addEventListener("playing", startPlayback);
    audioElement.addEventListener("seeked", startPlayback);
    audioElement.addEventListener("waiting", onWaiting);
    audioElement.addEventListener("error", onError);
    for (const event of ["seeking", "emptied"]) audioElement.addEventListener(event, resetToNeutral);
    audioElement.addEventListener("loadeddata", syncEnvelope);
    audioElement.addEventListener("pause", onPause);
    audioElement.addEventListener("ended", onEnded);

    return () => {
      disposed = true;
      decodedSrcRef.current = null;
      envelopeRef.current = null;
      audioElement.removeEventListener("play", startPlayback);
      audioElement.removeEventListener("playing", startPlayback);
      audioElement.removeEventListener("seeked", startPlayback);
      audioElement.removeEventListener("waiting", onWaiting);
      audioElement.removeEventListener("error", onError);
      for (const event of ["seeking", "emptied"]) audioElement.removeEventListener(event, resetToNeutral);
      audioElement.removeEventListener("loadeddata", syncEnvelope);
      audioElement.removeEventListener("pause", onPause);
      audioElement.removeEventListener("ended", onEnded);
      resetToNeutral();
    };
  }, [audioElement, alignment, morphTarget, additionalMorphTargets, enabled]);
}
